// secureTokenService.ts

import { isPlatform } from '@ionic/react';
import { UserProfileProps } from '../../api/auth/login';

/**
 * Asynchronously create or retrieve a SecureStorage instance in Cordova.
 * Returns null if not on Cordova.
 */
function createSecureStorage(): Promise<any | null> {
  return new Promise((resolve, reject) => {
    if (isPlatform('cordova')) {
      // Access SecureStorage from the global window object.
      // The type casting (window as any) is often needed in TypeScript.
      const SecureStoragePlugin = (window as any).SecureStorage;

      if (!SecureStoragePlugin) {
        reject(new Error('SecureStorage plugin not found.'));
        return;
      }

      const secureStorage = new SecureStoragePlugin(
        () => {
          resolve(secureStorage);
        },
        (error: any) => {
          reject(error);
        },
        'my_app_storage' // arbitrary name for the secure storage instance
      );
    } else {
      // Not on Cordova platform, return null so we can fall back to localStorage
      resolve(null);
    }
  });
}

/**
 * Store token using SecureStorage in Cordova or localStorage on Web.
 * @param token The token to store
 */
export async function storeToken(token: string): Promise<void> {
  const secureStorage = await createSecureStorage();

  // Wrap the token in an object and convert it to a JSON string
  const tokenData = JSON.stringify({ token });

  if (secureStorage) {
    // Cordova environment: use SecureStorage
    return new Promise((resolve, reject) => {
      secureStorage.set(
        () => resolve(),
        (error: any) => reject(error),
        'auth_token', // key
        tokenData // value (JSON string)
      );
    });
  } else {
    // Web environment: fall back to localStorage
    localStorage.setItem('auth_token', tokenData);
    return Promise.resolve();
  }
}

/**
 * Store auth data using SecureStorage in Cordova or localStorage on Web.
 * @param token The token to store
 */
export async function storeAuthData(data: any): Promise<void> {
  const secureStorage = await createSecureStorage();

  // Convert the data to a JSON string
  const jsonData = JSON.stringify(data);

  if (secureStorage) {
    // Cordova environment: use SecureStorage
    return new Promise((resolve, reject) => {
      secureStorage.set(
        () => resolve(),
        (error: any) => reject(error),
        'auth', // key
        jsonData // value (JSON string)
      );
    });
  } else {
    // Web environment: fall back to localStorage
    localStorage.setItem('auth', jsonData);
    return Promise.resolve();
  }
}

/**
 * Retrieve the token from SecureStorage in Cordova or from localStorage on Web.
 * @returns The token, or null if not found
 */
export async function getCordovaToken(): Promise<string | null> {
  const secureStorage = await createSecureStorage();

  if (secureStorage) {
    // Cordova environment: use SecureStorage
    return new Promise((resolve) => {
      secureStorage.get(
        (value: string) => {
          try {
            // Parse the JSON string if it exists
            const parsedValue = value ? JSON.parse(value) : null;
            resolve(parsedValue?.token || null); // Extract the token property
          } catch (error) {
            console.error('Error parsing JSON from SecureStorage:', error);
            resolve(null);
          }
        },
        (error: any) => {
          console.error('Error retrieving token from SecureStorage:', error);
          resolve(null);
        },
        'auth_token'
      );
    });
  } else {
    // Web environment: localStorage
    const authToken = localStorage.getItem('auth_token');
    try {
      // Parse the JSON string if it exists
      const parsedValue = authToken ? JSON.parse(authToken) : null;
      return parsedValue?.token || null; // Extract the token property
    } catch (error) {
      console.error('Error parsing JSON from localStorage:', error);
      return null;
    }
  }
}

/**
 * Retrieve the auth from SecureStorage in Cordova or from localStorage on Web.
 * @returns The auth, or null if not found
 */
export async function getCordovaAuthData(): Promise<UserProfileProps | null> {
  const secureStorage = await createSecureStorage();

  if (secureStorage) {
    // Cordova environment: use SecureStorage
    return new Promise((resolve) => {
      secureStorage.get(
        (value: string) => {
          try {
            // Parse the JSON string if it exists
            const parsedValue = value ? JSON.parse(value) : null;
            resolve(parsedValue);
          } catch (error) {
            console.error('Error parsing JSON from SecureStorage:', error);
            resolve(null);
          }
        },
        (error: any) => {
          console.error('Error retrieving token from SecureStorage:', error);
          resolve(null);
        },
        'auth'
      );
    });
  } else {
    // Web environment: localStorage
    const authData = localStorage.getItem('auth');
    try {
      // Parse the JSON string if it exists
      return authData ? JSON.parse(authData) : null;
    } catch (error) {
      console.error('Error parsing JSON from localStorage:', error);
      return null;
    }
  }
}

/**
 * Remove the token from SecureStorage in Cordova or from localStorage on Web.
 */
export async function removeCordovaToken(): Promise<void> {
  const secureStorage = await createSecureStorage();

  if (secureStorage) {
    // Cordova environment: use SecureStorage
    return new Promise((resolve, reject) => {
      secureStorage.remove(
        () => resolve(),
        (error: any) => {
          console.error('Error removing token from SecureStorage:', error);
          reject(error);
        },
        'auth_token'
      );
    });
  } else {
    // Web environment: localStorage
    localStorage.removeItem('auth_token');
    return Promise.resolve();
  }
}

/**
 * Remove the token from SecureStorage in Cordova or from localStorage on Web.
 */
export async function removeCordovaAuthData(): Promise<void> {
  const secureStorage = await createSecureStorage();

  if (secureStorage) {
    // Cordova environment: use SecureStorage
    return new Promise((resolve, reject) => {
      secureStorage.remove(
        () => resolve(),
        (error: any) => {
          console.error('Error removing token from SecureStorage:', error);
          reject(error);
        },
        'auth'
      );
    });
  } else {
    // Web environment: localStorage
    localStorage.removeItem('auth');
    return Promise.resolve();
  }
}
